/**
 * Shared schema defaults for Product Recommendation popup layout
 * (Recommendation Popup, Product Recommendation with Custom Rules).
 */
import {
  COVER_IMAGE_LAYOUT,
  DEFAULT_COVER_IMAGE_DISPLAY,
  DEFAULT_PRICE_DISPLAY,
  DEFAULT_QUANTITY_DISPLAY,
  DEFAULT_TRUST_BADGES_DISPLAY,
  DEFAULT_VARIANT_PICKER_DISPLAY,
  PRICE_LAYOUT,
  QUANTITY_LAYOUT,
  VARIANT_PICKER_STYLE,
  composeTrustBadgesText,
  getCoverImageCatalogSizes,
  getCoverImageDisplay,
  parseTrustBadgesText,
} from "./product-block-display.js";
import {
  PRODUCT_UPSELL_PRIMARY_CTA_ACTION,
  PRODUCT_UPSELL_PRIMARY_CTA_TEXT,
  normalizeProductUpsellProductSubsections,
  normalizeProductUpsellButtonSections,
  isUpsellRecommendationProductPopup,
} from "./product-upsell-recommendation-defaults.js";
import { isPopupLayout, POPUP_LAYOUT_IDS } from "./popup-layouts/registry.js";
import { ensureProductShellLayout, PRODUCT_SHELL_LAYOUT } from "./product-shell-layout.js";
import { setSectionVisibility } from "./section-visibility.js";
import { DEFAULT_BRAND_ATTRIBUTION_DISPLAY } from "./brand-attribution.js";
import {
  AI_RECOMMENDATION_CONTEXT_SOURCE,
  AI_RECOMMENDATION_GENERATION_MODE,
  DEFAULT_AI_RECOMMENDATION_META,
  DEFAULT_AI_RECOMMENDATION_STATIC_BANNER_TEXT,
} from "./ai-recommendation-copy.js";
import { applyAiRecommendationUpsellBannerDefaults } from "./ai-recommendation-upsell-schema.js";
import {
  DEFAULT_PITCH_DISPLAY,
} from "./pitch-layout-display.js";

export const PRODUCT_RECOMMENDATION_LAYOUT_ID =
  POPUP_LAYOUT_IDS.PRODUCT_RECOMMENDATION;

export const PRODUCT_RECOMMENDATION_HEADLINE_SAMPLE =
  "Pairs perfectly with what you're viewing";

export const PRODUCT_RECOMMENDATION_TRUST_BADGES_TEXT =
  "30-day returns  ·  Free shipping  ·  Secure checkout";

/** Default popup shell width (px) — desktop vs mobile preview. */
export const PRODUCT_RECOMMENDATION_SHELL_WIDTH_DESKTOP_PX = 680;
export const PRODUCT_RECOMMENDATION_SHELL_WIDTH_MOBILE_PX = 320;
export const PRODUCT_RECOMMENDATION_IMAGE_WIDTH_PX = 145;
export const PRODUCT_RECOMMENDATION_IMAGE_HEIGHT_PX = 132;
/** @deprecated Total slot height is hero + thumbs; use getProductRecommendationImageSlotHeight(). */
export const PRODUCT_RECOMMENDATION_IMAGE_GALLERY_HEIGHT_PX = 196;
export const PRODUCT_RECOMMENDATION_IMAGE_MOBILE_WIDTH_PX = 155;
export const PRODUCT_RECOMMENDATION_IMAGE_MOBILE_HEIGHT_PX = 155;
/** @deprecated Total slot height is hero + thumbs; use getProductRecommendationImageSlotHeight(). */
export const PRODUCT_RECOMMENDATION_IMAGE_MOBILE_GALLERY_HEIGHT_PX = 210;
export const PRODUCT_RECOMMENDATION_IMAGE_MOBILE_TWIN_HEIGHT_PX = 155;
/** @deprecated Use PRODUCT_RECOMMENDATION_IMAGE_WIDTH_PX */
export const PRODUCT_RECOMMENDATION_IMAGE_SIZE_PX =
  PRODUCT_RECOMMENDATION_IMAGE_WIDTH_PX;

/**
 * Hero image frame height (px) — same for single-image and gallery cards.
 * @param {object} [section] COVER_IMAGE subsection
 * @param {boolean} [isMobile]
 */
export function getProductRecommendationHeroImageHeight(section, isMobile = false) {
  const sizes = getCoverImageCatalogSizes(section, isMobile);
  const fallback = isMobile
    ? PRODUCT_RECOMMENDATION_IMAGE_MOBILE_HEIGHT_PX
    : PRODUCT_RECOMMENDATION_IMAGE_HEIGHT_PX;
  const raw = Number(sizes?.mainHeight);
  if (!Number.isFinite(raw) || raw <= 0) return fallback;
  if (raw > fallback + 48) return fallback;
  return raw;
}

/**
 * Total image-wrap height: hero only, or hero + thumb strip when gallery + 2+ images.
 * @param {object} [section]
 * @param {boolean} [isMobile]
 * @param {{ imageCount?: number, layout?: string }} [options]
 */
export function getProductRecommendationImageSlotHeight(
  section,
  isMobile = false,
  { imageCount = 1, layout } = {}
) {
  const display = getCoverImageDisplay(section);
  const resolvedLayout = layout || display.layout;
  const isGallery =
    resolvedLayout === COVER_IMAGE_LAYOUT.HERO_THUMBNAILS ||
    resolvedLayout === COVER_IMAGE_LAYOUT.HORIZONTAL_SLIDER;
  const heroHeight = getProductRecommendationHeroImageHeight(section, isMobile);
  if (!isGallery || imageCount <= 1) {
    return heroHeight;
  }
  const sizes = getCoverImageCatalogSizes(section, isMobile);
  const gap = Math.max(0, Number(display.gap) || 8);
  const thumbHeight = Number(sizes?.thumbHeight) || (isMobile ? 36 : 40);
  return heroHeight + thumbHeight + gap;
}

export const PRODUCT_RECOMMENDATION_DEFAULT_OPTIONS = Object.freeze({
  showTrustBadges: true,
  buttonText: PRODUCT_UPSELL_PRIMARY_CTA_TEXT,
  shellWidthDesktop: PRODUCT_RECOMMENDATION_SHELL_WIDTH_DESKTOP_PX,
  shellWidthMobile: PRODUCT_RECOMMENDATION_SHELL_WIDTH_MOBILE_PX,
  productCardBorder: Object.freeze({
    enabled: true,
    color: "#d4c4b0",
    width: 1,
    radius: 10,
  }),
});

const HEADLINE_SERIF_FONT =
  '"Libre Baskerville", Georgia, "Times New Roman", serif';

const HEADLINE_OVERRIDE_DESKTOP = `.css{
  font-family: ${HEADLINE_SERIF_FONT};
  text-align: center;
  justify-content: center;
}`;

const HEADLINE_OVERRIDE_MOBILE = `.css{
  font-family: ${HEADLINE_SERIF_FONT};
  text-align: center;
  justify-content: center;
}`;

const PREMIUM_PRICE_HTML =
  '<div class="price-container"><span class="price">{{ currency }} {{ discount_price }}</span></div>';

const PREMIUM_PRICE_OVERRIDE_DESKTOP = `.price-container{
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: flex-start;
  gap: 8px;
}
.price {
  font-weight: 700;
  font-size: 14px;
  letter-spacing: -0.01em;
  color: #0f172a;
}
.compare-price {
  text-decoration: line-through;
  color: #64748b;
  font-size: 12px;
}
`;

const PREMIUM_PRICE_OVERRIDE_MOBILE = `.price-container{
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: flex-start;
  gap: 6px;
}
.price {
  font-weight: 700;
  font-size: 14px;
  letter-spacing: -0.01em;
  color: #0f172a;
}
.compare-price {
  text-decoration: line-through;
  color: #64748b;
  font-size: 11px;
}
`;

/** Canonical vertical product block subsections (image → CTA). */
export function buildVerticalProductSubsections() {
  return [
    {
      type: "COVER_IMAGE",
      src: { image: "", imageNames: [], images: [] },
      backgroundOpacity: 1,
      objectFit: "contain",
      objectPosition: "center",
      setting: {
        display: {
          ...DEFAULT_COVER_IMAGE_DISPLAY,
          layout: COVER_IMAGE_LAYOUT.SINGLE,
          autoImageHeight: false,
          sizes: {
            desktop: {
              columnWidth: PRODUCT_RECOMMENDATION_IMAGE_WIDTH_PX,
              mainHeight: PRODUCT_RECOMMENDATION_IMAGE_HEIGHT_PX,
              thumbWidth: 44,
              thumbHeight: 44,
            },
            mobile: {
              columnWidth: PRODUCT_RECOMMENDATION_IMAGE_MOBILE_WIDTH_PX,
              mainHeight: PRODUCT_RECOMMENDATION_IMAGE_MOBILE_HEIGHT_PX,
              thumbWidth: 36,
              thumbHeight: 36,
            },
          },
        },
        customCSS: {
          desktop: { borderRadius: 8, objectFit: "contain" },
          mobile: { borderRadius: 8, objectFit: "contain" },
        },
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: "TITLE",
      text: "{{title}}",
      setting: {
        customCSS: {
          desktop: {
            fontSize: "14px",
            fontWeight: "600",
            lineHeight: "1.35",
            color: "#0f172a",
            textAlign: "left",
            justifyContent: "flex-start",
          },
          mobile: {
            fontSize: "14px",
            fontWeight: "600",
            lineHeight: "1.35",
            color: "#0f172a",
            textAlign: "left",
            justifyContent: "flex-start",
          },
        },
        overrideCss: { desktop: "", mobile: "" },
      },
      AIText: [],
    },
    {
      type: "PRICE",
      content: PREMIUM_PRICE_HTML,
      setting: {
        display: {
          ...DEFAULT_PRICE_DISPLAY,
          layout: PRICE_LAYOUT.INLINE_ROW,
        },
        overrideCss: {
          desktop: PREMIUM_PRICE_OVERRIDE_DESKTOP,
          mobile: PREMIUM_PRICE_OVERRIDE_MOBILE,
        },
        customCSS: {
          desktop: { textAlign: "left", justifyContent: "flex-start" },
          mobile: { textAlign: "left", justifyContent: "flex-start" },
        },
      },
    },
    {
      type: "VARIANT_PICKER",
      search: { placeholder: "Search options…" },
      setting: {
        display: {
          ...DEFAULT_VARIANT_PICKER_DISPLAY,
          variantPickerVariant: VARIANT_PICKER_STYLE.PREMIUM_DROPDOWN,
          label: "",
        },
        variantPickerVariant: VARIANT_PICKER_STYLE.PREMIUM_DROPDOWN,
        customCSS: {
          desktop: {
            fontSize: "12px",
            fontWeight: "500",
            color: "#0f172a",
            backgroundColor: "#ffffff",
            borderColor: "#d4c4b0",
          },
          mobile: {
            fontSize: "12px",
            fontWeight: "500",
            color: "#0f172a",
            backgroundColor: "#ffffff",
            borderColor: "#d4c4b0",
          },
        },
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: "QUANTITY_PICKER",
      min: "1",
      max: "99",
      setting: {
        display: {
          ...DEFAULT_QUANTITY_DISPLAY,
          layout: QUANTITY_LAYOUT.PREMIUM_COMPACT,
          label: "",
          showIncentiveBadge: false,
        },
        customCSS: {
          desktop: {
            display: "none",
            fontSize: "13px",
            fontWeight: "600",
            color: "#0f172a",
            backgroundColor: "#ffffff",
            borderStyle: "solid",
            borderWidth: 1,
            borderColor: "#d4c4b0",
            borderRadius: 8,
          },
          mobile: {
            display: "none",
            fontSize: "12px",
            fontWeight: "600",
            color: "#0f172a",
            backgroundColor: "#ffffff",
            borderStyle: "solid",
            borderWidth: 1,
            borderColor: "#d4c4b0",
            borderRadius: 8,
          },
        },
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: "BUTTON",
      text: PRODUCT_UPSELL_PRIMARY_CTA_TEXT,
      setting: {
        action: { ...PRODUCT_UPSELL_PRIMARY_CTA_ACTION },
        isConversationEnabled: true,
        customCSS: {
          desktop: {
            backgroundColor: "#0f172a",
            color: "#ffffff",
            borderRadius: 6,
            fontSize: "14px",
            fontWeight: "600",
            letterSpacing: "0.01em",
            width: "100%",
          },
          mobile: {
            backgroundColor: "#0f172a",
            color: "#ffffff",
            borderRadius: 6,
            fontSize: "14px",
            fontWeight: "600",
            letterSpacing: "0.01em",
            width: "100%",
          },
        },
        overrideCss: { desktop: "", mobile: "" },
      },
    },
  ];
}

export function buildProductRecommendationTrustBadgesSection(
  text = PRODUCT_RECOMMENDATION_TRUST_BADGES_TEXT
) {
  const display = {
    ...DEFAULT_TRUST_BADGES_DISPLAY,
    items: parseTrustBadgesText(text).length
      ? parseTrustBadgesText(text)
      : DEFAULT_TRUST_BADGES_DISPLAY.items,
  };
  return {
    type: "TRUST_BADGES",
    text: composeTrustBadgesText(display) || text,
    setting: {
      role: "trust",
      display,
      customCSS: {
        desktop: {
          fontSize: "14px",
          fontWeight: "500",
          color: "#475569",
          justifyContent: "center",
        },
        mobile: {
          fontSize: "12px",
          fontWeight: "500",
          color: "#475569",
          justifyContent: "center",
        },
      },
      overrideCss: { desktop: "", mobile: "" },
    },
    AIText: [],
  };
}

export function buildProductRecommendationPitchSection(
  text = DEFAULT_AI_RECOMMENDATION_STATIC_BANNER_TEXT
) {
  const section = {
    type: "TEXT",
    text,
    setting: {
      role: "aiBanner",
      pitchLayout: true,
      display: { ...DEFAULT_PITCH_DISPLAY },
      customCSS: {
        desktop: {
          fontSize: "14px",
          fontWeight: "600",
          fontFamily: HEADLINE_SERIF_FONT,
          color: "#0f172a",
          textAlign: "center",
          justifyContent: "center",
          lineHeight: "1.35",
          letterSpacing: "-0.015em",
        },
        mobile: {
          fontSize: "14px",
          fontWeight: "600",
          fontFamily: HEADLINE_SERIF_FONT,
          color: "#0f172a",
          textAlign: "center",
          justifyContent: "center",
          lineHeight: "1.35",
        },
      },
      overrideCss: {
        desktop: HEADLINE_OVERRIDE_DESKTOP,
        mobile: HEADLINE_OVERRIDE_MOBILE,
      },
    },
    aiMeta: {
      generationMode: AI_RECOMMENDATION_GENERATION_MODE.STATIC,
      useAiGeneration: false,
      contextSources: [
        AI_RECOMMENDATION_CONTEXT_SOURCE.CURRENT_PRODUCT,
        AI_RECOMMENDATION_CONTEXT_SOURCE.OFFER_PRODUCT,
        AI_RECOMMENDATION_CONTEXT_SOURCE.BROWSING,
      ],
      storageKeys: [...DEFAULT_AI_RECOMMENDATION_META.storageKeys],
      labelLine: "AI RECOMMENDATION",
      maxWords: 22,
      prompt:
        "Write a personalized product recommendation for the shopper viewing multiple complementary picks. " +
        "First line must be exactly: AI RECOMMENDATION. " +
        "Second line: one compelling sentence explaining why these recommended products fit their cart or what they are viewing. " +
        "Refer to the group of products (these picks, this bundle), not a single SKU. " +
        "Be specific, friendly, and under 22 words on line 2. Do not use quotes or markdown.",
    },
    AIText: [],
  };
  applyAiRecommendationUpsellBannerDefaults(section);
  return section;
}

export function buildProductRecommendationHeadlineSection(
  text = PRODUCT_RECOMMENDATION_HEADLINE_SAMPLE
) {
  return {
    type: "TEXT",
    text,
    setting: {
      role: "headline",
      customCSS: {
        desktop: {
          fontSize: "22px",
          fontWeight: "600",
          fontFamily: HEADLINE_SERIF_FONT,
          color: "#0f172a",
          textAlign: "center",
          justifyContent: "center",
          lineHeight: "1.25",
          letterSpacing: "-0.02em",
        },
        mobile: {
          fontSize: "17px",
          fontWeight: "600",
          fontFamily: HEADLINE_SERIF_FONT,
          color: "#0f172a",
          textAlign: "center",
          justifyContent: "center",
          lineHeight: "1.3",
        },
      },
      overrideCss: {
        desktop: HEADLINE_OVERRIDE_DESKTOP,
        mobile: HEADLINE_OVERRIDE_MOBILE,
      },
    },
    AIText: [],
  };
}

export function buildProductRecommendationBrandAttributionSection() {
  return {
    type: "BRAND_ATTRIBUTION",
    text: "",
    setting: {
      locked: true,
      role: "brandAttribution",
      display: { ...DEFAULT_BRAND_ATTRIBUTION_DISPLAY },
      customCSS: {
        desktop: {
          fontSize: "11px",
          fontWeight: "400",
          color: "#64748b",
          justifyContent: "center",
          padding: "0 14px 10px",
        },
        mobile: {
          fontSize: "10px",
          color: "#64748b",
          padding: "0 12px 8px",
        },
      },
      dimension: {
        desktop: { width: "100%", height: "auto" },
        mobile: { width: "100%", height: "auto" },
      },
      overrideCss: { desktop: "", mobile: "" },
    },
    AIText: [],
  };
}

/**
 * @param {object[] | null | undefined} sections
 */
export function ensureProductRecommendationTrustBadges(sections) {
  if (!Array.isArray(sections)) return;
  const hasTrust = sections.some(
    (s) => s?.type === "TRUST_BADGES" || s?.setting?.role === "trust"
  );
  if (!hasTrust) {
    sections.push(buildProductRecommendationTrustBadgesSection());
  }
}

export function ensureProductRecommendationBrandAttribution(sections) {
  if (!Array.isArray(sections)) return;
  const hasBrand = sections.some(
    (s) =>
      s?.type === "BRAND_ATTRIBUTION" ||
      s?.setting?.role === "brandAttribution"
  );
  if (!hasBrand) {
    sections.push(buildProductRecommendationBrandAttributionSection());
  }
}

/**
 * @param {object | null | undefined} product
 * @param {object | null | undefined} [popup]
 */
export function normalizeProductRecommendationProductSubsections(product, popup) {
  if (!product) return;
  normalizeProductUpsellProductSubsections(product, { catalogAlign: false });

  if (product.sections) {
    for (const sub of product.sections) {
      if (sub.type === "TITLE" || sub.type === "PRICE") {
        sub.setting = sub.setting || {};
        sub.setting.customCSS = sub.setting.customCSS || {};
        for (const view of ["desktop", "mobile"]) {
          const existing = sub.setting.customCSS[view] || {};
          sub.setting.customCSS[view] = {
            ...existing,
            textAlign: "left",
            justifyContent: "flex-start",
          };
        }
      }
      if (sub.type === "QUANTITY_PICKER") {
        const desktopDisplay = sub?.setting?.customCSS?.desktop?.display;
        const mobileDisplay = sub?.setting?.customCSS?.mobile?.display;
        const explicitlyVisible =
          desktopDisplay &&
          desktopDisplay !== "none" &&
          mobileDisplay &&
          mobileDisplay !== "none";

        if (!explicitlyVisible) {
          setSectionVisibility(sub, false);
        }

        if (sub?.setting?.display?.layout !== QUANTITY_LAYOUT.PREMIUM_COMPACT) {
          sub.setting.display = {
            ...(sub.setting.display || {}),
            layout: QUANTITY_LAYOUT.PREMIUM_COMPACT,
          };
        }
      }
    }
  }

  const hasButton = product.sections?.some((s) => s?.type === "BUTTON");
  if (!hasButton) {
    const defaults = buildVerticalProductSubsections();
    const buttonDefault = defaults.find((s) => s.type === "BUTTON");
    if (buttonDefault) {
      product.sections = [...(product.sections || []), buttonDefault];
    }
  }

  product.setting = product.setting || {};
  product.setting.layoutFlow = true;
  product.setting.layout = {
    ...(product.setting.layout || {}),
    preset: PRODUCT_RECOMMENDATION_LAYOUT_ID,
    imagePosition: "top",
    density: "vertical",
  };
  ensureProductShellLayout(product, popup);

  if (product.sections) {
    for (const sub of product.sections) {
      if (sub?.type !== "COVER_IMAGE") continue;
      sub.objectFit = sub.objectFit || "cover";
      sub.setting = sub.setting || {};
      // Recommendation popup: single full-bleed image only (no strip / gallery UI).
      const layout = COVER_IMAGE_LAYOUT.SINGLE;
      sub.setting.display = {
        ...(sub.setting.display || {}),
        layout,
        autoImageHeight: true,
        sizes: {
          desktop: {
            columnWidth: PRODUCT_RECOMMENDATION_IMAGE_WIDTH_PX,
            mainHeight: PRODUCT_RECOMMENDATION_IMAGE_HEIGHT_PX,
            thumbWidth: 40,
            thumbHeight: 40,
            ...(sub.setting.display?.sizes?.desktop || {}),
          },
          mobile: {
            columnWidth: PRODUCT_RECOMMENDATION_IMAGE_MOBILE_WIDTH_PX,
            mainHeight: PRODUCT_RECOMMENDATION_IMAGE_MOBILE_HEIGHT_PX,
            thumbWidth: 36,
            thumbHeight: 36,
            ...(sub.setting.display?.sizes?.mobile || {}),
          },
        },
      };
    }
  }
}

/**
 * @param {object[] | null | undefined} sections
 * @param {object | null | undefined} [popup]
 */
export function normalizeProductRecommendationSections(sections, popup) {
  if (!Array.isArray(sections)) return;

  const headline = sections.find(
    (s) =>
      s?.type === "TEXT" &&
      (s?.setting?.role === "headline" || !s?.setting?.role)
  );
  if (headline) {
    if (!headline.setting?.role) {
      headline.setting = headline.setting || {};
      headline.setting.role = "headline";
    }
    headline.setting.customCSS = headline.setting.customCSS || {};
    for (const view of ["desktop", "mobile"]) {
      const existing = headline.setting.customCSS[view] || {};
      headline.setting.customCSS[view] = {
        ...existing,
        textAlign: "center",
        justifyContent: "center",
        fontFamily: existing.fontFamily || HEADLINE_SERIF_FONT,
        ...(view === "desktop" && !existing.fontSize
          ? { fontSize: "22px", fontWeight: "600", lineHeight: "1.25" }
          : {}),
        ...(view === "mobile" && !existing.fontSize
          ? { fontSize: "17px", fontWeight: "600", lineHeight: "1.3" }
          : {}),
      };
    }
    headline.setting.overrideCss = {
      desktop: HEADLINE_OVERRIDE_DESKTOP,
      mobile: HEADLINE_OVERRIDE_MOBILE,
      ...(headline.setting.overrideCss || {}),
    };
  }

  const product = sections.find((s) => s?.type === "PRODUCT");
  normalizeProductRecommendationProductSubsections(product, popup);
  normalizeProductUpsellButtonSections(sections);
  ensureProductRecommendationTrustBadges(sections);
  ensureProductRecommendationBrandAttribution(sections);
}

/**
 * @param {object | null | undefined} generalSettings
 */
function mergeProductRecommendationOptions(raw = {}) {
  const border = {
    ...PRODUCT_RECOMMENDATION_DEFAULT_OPTIONS.productCardBorder,
    ...(raw.productCardBorder && typeof raw.productCardBorder === "object"
      ? raw.productCardBorder
      : {}),
  };
  return {
    ...PRODUCT_RECOMMENDATION_DEFAULT_OPTIONS,
    ...raw,
    productCardBorder: border,
  };
}

/**
 * @param {object | null | undefined} popupData
 */
export function getProductRecommendationLayoutOptionsFromPopup(popupData) {
  const gs = popupData?.generalSettings || {};
  const raw = gs.layoutConfig?.options || gs.layoutTree?.options || {};
  return mergeProductRecommendationOptions(raw);
}

function ensureProductRecommendationPopupShell(step) {
  if (!step || typeof step !== "object") return;
  step.popupSettings = step.popupSettings || {};
  step.popupSettings.customCSS = step.popupSettings.customCSS || {};

  for (const [view, widthPx] of [
    ["desktop", PRODUCT_RECOMMENDATION_SHELL_WIDTH_DESKTOP_PX],
    ["mobile", PRODUCT_RECOMMENDATION_SHELL_WIDTH_MOBILE_PX],
  ]) {
    const existing = step.popupSettings.customCSS[view] || {};
    if (existing.width == null || existing.width === "") {
      step.popupSettings.customCSS[view] = {
        ...existing,
        width: widthPx,
        widthUnit: "px",
      };
    }
  }
}

export function normalizeProductRecommendationLayoutOptions(generalSettings) {
  if (!generalSettings || typeof generalSettings !== "object") return;

  generalSettings.popupLayoutId = PRODUCT_RECOMMENDATION_LAYOUT_ID;
  generalSettings.productShellLayout = PRODUCT_SHELL_LAYOUT.VERTICAL;
  generalSettings.lockDraggable = true;
  generalSettings.isDraggable = false;

  generalSettings.layoutConfig = generalSettings.layoutConfig || { version: 1 };
  const options = mergeProductRecommendationOptions(
    generalSettings.layoutConfig.options || {}
  );
  generalSettings.layoutConfig = {
    version: generalSettings.layoutConfig.version ?? 1,
    options,
  };
}

/**
 * @param {object} popup
 * @returns {object}
 */
export function normalizeProductRecommendationPopupSchema(popup) {
  if (!popup || typeof popup !== "object") return popup;

  const clone =
    typeof structuredClone === "function"
      ? structuredClone(popup)
      : JSON.parse(JSON.stringify(popup));

  normalizeProductRecommendationLayoutOptions(clone.generalSettings);

  const steps = clone.schema;
  if (!Array.isArray(steps)) return clone;

  for (const step of steps) {
    ensureProductRecommendationPopupShell(step);
    const sections = step?.section || step?.sections;
    if (!Array.isArray(sections)) continue;
    normalizeProductRecommendationSections(sections, clone);
    step.section = sections;
  }

  return clone;
}

export function isProductRecommendationLayout(popup) {
  return isPopupLayout(popup, PRODUCT_RECOMMENDATION_LAYOUT_ID);
}

/**
 * Detect product-recommendation renderer even when popupLayoutId is missing.
 * @param {object | null | undefined} popup
 */
export function isProductRecommendationPopupLayout(popup) {
  if (isProductRecommendationLayout(popup)) {
    return true;
  }
  if (isPopupLayout(popup, POPUP_LAYOUT_IDS.AI_RECOMMENDATION_UPSELL)) {
    return false;
  }

  const popUpType = String(popup?.popUpType || "").trim().toLowerCase();
  if (popUpType === "recommendation popup") {
    return true;
  }

  const step = popup?.schema?.[0];
  const sections = step?.section || step?.sections || [];
  if (
    Array.isArray(sections) &&
    sections.some(
      (section) =>
        section?.type === "TEXT" && section?.setting?.pitchLayout === true
    )
  ) {
    return true;
  }

  return isUpsellRecommendationProductPopup(popup);
}
